import type React from 'react';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { AppLayout, Navbar, Sidebar } from '@heroui-pro/react';
import { useTranslation } from 'react-i18next';
import { useNavbarSlot } from '../navbarSlot';
import { DashboardLanguageToggle } from './DashboardLanguageToggle';
import { useControlCenter } from '@/pages/controlCenter';
import { DashboardThemeToggle } from './DashboardThemeToggle';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type DashboardNavbarProps = {
    onBack: () => void;
    showBack: boolean;
    title: string;
};

type LayoutIconButtonProps = {
    icon: string;
    id: string;
    label: string;
    onPress?: () => void;
};

const LayoutIconButton: React.FC<LayoutIconButtonProps> = ({
    icon,
    id,
    label,
    onPress,
}) => {
    return (
        <Button
            className="min-w-0"
            data-testid={id}
            id={id}
            isIconOnly
            onPress={onPress}
            size="sm"
            variant="ghost"
        >
            <Icon
                className="size-4"
                data-testid={`${id}-icon`}
                icon={icon}
                id={`${id}-icon`}
            />
            <span
                className="sr-only"
                {...getNodeTestIdProp(`${id}-label`)}
                id={`${id}-label`}
            >
                {label}
            </span>
        </Button>
    );
};

export const DashboardNavbar: React.FC<DashboardNavbarProps> = ({
    onBack,
    showBack,
    title,
}) => {
    const { t } = useTranslation('components');
    const { onOpenPalette } = useControlCenter();
    // A page may hang one node off the title — the template editor's version
    // chip. Empty on every other screen; see `navbarSlot`.
    const accessory = useNavbarSlot();
    return (
        <Navbar
            data-testid="dashboard-navbar"
            id="dashboard-navbar"
            maxWidth="full"
        >
            <Navbar.Header
                data-testid="dashboard-navbar-header"
                id="dashboard-navbar-header"
            >
                <AppLayout.MenuToggle
                    data-testid="dashboard-navbar-menu-toggle"
                    id="dashboard-navbar-menu-toggle"
                />
                <Sidebar.Trigger
                    data-testid="dashboard-navbar-sidebar-trigger"
                    id="dashboard-navbar-sidebar-trigger"
                />
                {showBack ? (
                    <Button
                        data-testid="dashboard-navbar-back-button"
                        id="dashboard-navbar-back-button"
                        onPress={onBack}
                        variant="ghost"
                        isIconOnly
                    >
                        <Icon
                            className="size-6"
                            data-testid="dashboard-navbar-back-button-icon"
                            icon="boxicons:arrow-left-circle"
                            id="dashboard-navbar-back-button-icon"
                        />
                    </Button>
                ) : null}
                <h1
                    className="truncate text-xl font-semibold text-foreground"
                    {...getNodeTestIdProp('dashboard-navbar-title')}
                    id="dashboard-navbar-title"
                >
                    {title}
                </h1>
                {accessory ? (
                    <div
                        className="flex shrink-0 items-center"
                        {...getNodeTestIdProp('dashboard-navbar-accessory')}
                        id="dashboard-navbar-accessory"
                    >
                        {accessory}
                    </div>
                ) : null}

                <Navbar.Spacer
                    data-testid="dashboard-navbar-spacer"
                    id="dashboard-navbar-spacer"
                />
                <div
                    className="hidden items-center gap-2 sm:flex"
                    {...getNodeTestIdProp('dashboard-navbar-actions')}
                    id="dashboard-navbar-actions"
                >
                    <LayoutIconButton
                        icon="solar:magnifer-linear"
                        id="dashboard-navbar-search-button"
                        onPress={onOpenPalette}
                        label={t('layout.actions.search')}
                    />
                    <LayoutIconButton
                        icon="solar:bell-linear"
                        id="dashboard-navbar-notifications-button"
                        label={t('layout.actions.notifications')}
                    />
                    <DashboardLanguageToggle
                        className="gap-0"
                        id="dashboard-navbar-language-toggle"
                        itemIdPrefix="dashboard-navbar"
                    />
                    <DashboardThemeToggle
                        className="gap-0"
                        id="dashboard-navbar-theme-toggle"
                        itemIdPrefix="dashboard-navbar"
                    />
                </div>
            </Navbar.Header>
        </Navbar>
    );
};
